import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '@/stores/auth';
import { matchStudentEmailDomain } from '@makhe/shared';
import { callRecordLegalConsent } from '@/lib/callables';
import { LEGAL_VERSIONS } from '@makhe/shared';
import { AuthLayout } from './AuthLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { friendlyError } from '@/lib/friendlyError';

const schema = z.object({
  displayName: z
    .string()
    .min(2, 'Min 2 characters')
    .max(60)
    .refine((v) => v.trim().split(/\s+/).filter(Boolean).length >= 2, {
      message: 'Enter your name and surname.',
    }),
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Min 8 characters'),
  acceptedTerms: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the terms.' }),
  }),
});
type FormValues = z.infer<typeof schema>;

export default function SignUpPage() {
  const { firebaseUser, initialised } = useAuth();
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    const ref = params.get('ref');
    if (ref) sessionStorage.setItem('makhe.referrer', ref);
  }, [params]);
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const nextParam = params.get('next');
  const defaultNext =
    firebaseUser?.email && !matchStudentEmailDomain(firebaseUser.email)
      ? '/profile/verify-student'
      : '/feed';
  const next = (nextParam && decodeURIComponent(nextParam)) || defaultNext;
  const signInHref = nextParam ? `/auth/sign-in?next=${nextParam}` : '/auth/sign-in';

  if (initialised && firebaseUser) {
    return <Navigate to={next} replace />;
  }

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, values.email, values.password);
      await updateProfile(cred.user, { displayName: values.displayName });
      // onUserCreate may race ahead of updateProfile and snapshot the email local-part
      // (student number). Always persist the name the user typed.
      await setDoc(
        doc(db, 'users', cred.user.uid),
        { displayName: values.displayName, updatedAt: serverTimestamp() },
        { merge: true },
      );
      await callRecordLegalConsent({
        termsVersion: LEGAL_VERSIONS.terms,
        privacyVersion: LEGAL_VERSIONS.privacy,
      });
      toast.success('Welcome to Makhe!');
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = async () => {
    setSubmitting(true);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Use your student email so we can verify you instantly."
      footer={
        <>
          Already have an account?{' '}
          <Link to={signInHref} className="text-makhe-primary">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Full name"
          autoComplete="name"
          placeholder="e.g. Thandi Ndlovu"
          hint="Name and surname, as on your student card. We use this when verifying you."
          {...register('displayName')}
          error={errors.displayName?.message}
        />
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          {...register('email')}
          hint="Use your @students.uni email if you have one. We'll verify you automatically."
          error={errors.email?.message}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          {...register('password')}
          error={errors.password?.message}
        />
        <label className="flex items-start gap-2 text-xs text-makhe-ink/70">
          <input type="checkbox" {...register('acceptedTerms')} className="mt-0.5" />
          <span>
            I agree to the{' '}
            <Link to="/legal/terms" className="underline">
              Terms
            </Link>{' '}
            and{' '}
            <Link to="/legal/privacy" className="underline">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link to="/legal/refunds" className="underline">
              Refund Policy
            </Link>
            .
          </span>
        </label>
        {errors.acceptedTerms && (
          <span className="text-xs text-red-400">{errors.acceptedTerms.message}</span>
        )}
        <Button type="submit" loading={submitting} className="w-full">
          Create account
        </Button>
      </form>
      <div className="relative my-2 flex items-center text-xs text-makhe-muted/60">
        <div className="h-px flex-1 bg-makhe-line/50" />
        <span className="px-3">or</span>
        <div className="h-px flex-1 bg-makhe-line/50" />
      </div>
      <Button type="button" variant="secondary" className="w-full" onClick={onGoogle} disabled={submitting}>
        Continue with Google
      </Button>
    </AuthLayout>
  );
}
